import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { AssistancePanelProvider } from './contexts/AssistancePanelContext';
import { Toaster } from './components/ui/sonner';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import WelcomePage from './pages/WelcomePage';
import DashboardPage from './pages/DashboardPage';
import PhasePage from './pages/PhasePage';
import SettingsPage from './pages/SettingsPage';
import AccountPage from './pages/AccountPage';
import AdminPage from './pages/AdminPage';
import NoEditionPage from './pages/NoEditionPage';
import CockpitOverviewPage from './pages/CockpitOverviewPage';
import CockpitExplorerPage from './pages/CockpitExplorerPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import SupportPage from './pages/SupportPage';
import DownloadsPage from './pages/DownloadsPage';
import IdeaPage from './pages/IdeaPage';
import WhatsNewPage from './pages/WhatsNewPage';

import { AnnouncementProvider } from './contexts/AnnouncementContext';

import './App.css';

// Loading spinner component
const LoadingSpinner = () => (
  <div className="min-h-screen bg-cockpit-bg flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-airbus-blue border-t-transparent rounded-full animate-spin" />
  </div>
);

// Protected route wrapper
const ProtectedRoute = ({ children, requireFounder = false }) => {
  const { user, hasEdition, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check for forced password change
  if (user.force_password_change) {
    return <Navigate to="/change-password" replace />;
  }

  // FOUNDER BYPASS: Founders skip ALL onboarding flows
  // They always go directly to dashboard with full access
  if (user.role === 'founder') {
    if (requireFounder) {
      return children; // Allow access to founder-only routes
    }
    return children; // Bypass all other checks for founders
  }

  // Check for onboarding (display name) - non-founders only
  if (!user.display_name && !user.is_onboarded) {
    return <Navigate to="/welcome" replace />;
  }

  // Check for founder access requirement
  if (requireFounder && user.role !== 'founder') {
    return <Navigate to="/dashboard" replace />;
  }

  // NOTE: We removed hasEdition check here
  // Users should reach dashboard first, then see paywall when clicking locked phases
  // This allows beta testers to access the app before manual activation

  return children;
};

// Public route wrapper (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (user) {
    if (user.force_password_change) {
      return <Navigate to="/change-password" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Setup route removed - onboarding now ends at /dashboard after /welcome

// No edition route - renders the purchase screen for free users.
// Paid users / founders are redirected straight to the dashboard.
const NoEditionRoute = ({ children }) => {
  const { user, loading, hasCoreAccess } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'founder' || hasCoreAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Change password route
const ChangePasswordRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If no forced password change, redirect to dashboard
  if (!user.force_password_change) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Welcome / Onboarding route
const WelcomeRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If already onboarded, redirect to dashboard
  if (user.display_name || user.is_onboarded) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Payment routes (authenticated but doesn't require edition)
const PaymentRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Change password (forced) */}
      <Route path="/change-password" element={<ChangePasswordRoute><ChangePasswordPage /></ChangePasswordRoute>} />

      {/* Welcome / Onboarding route */}
      <Route path="/welcome" element={<WelcomeRoute><WelcomePage /></WelcomeRoute>} />

      {/* No edition route */}
      <Route path="/no-edition" element={<NoEditionRoute><NoEditionPage /></NoEditionRoute>} />

      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/phases/:slug" element={<ProtectedRoute><PhasePage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />

      {/* Cockpit Exploration routes */}
      <Route path="/cockpit" element={<ProtectedRoute><CockpitOverviewPage /></ProtectedRoute>} />
      <Route path="/cockpit/:zone" element={<ProtectedRoute><CockpitExplorerPage /></ProtectedRoute>} />

      {/* Admin route (founder only) */}
      <Route path="/admin" element={<ProtectedRoute requireFounder><AdminPage /></ProtectedRoute>} />

      {/* Payment routes (authenticated but doesn't require edition) */}
      <Route path="/payment-success" element={<PaymentRoute><PaymentSuccessPage /></PaymentRoute>} />
      <Route path="/payment-cancel" element={<PaymentRoute><PaymentCancelPage /></PaymentRoute>} />

      {/* Support & Feedback routes */}
      <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
      <Route path="/ideas" element={<ProtectedRoute><IdeaPage /></ProtectedRoute>} />
      <Route path="/downloads" element={<ProtectedRoute><DownloadsPage /></ProtectedRoute>} />

      {/* What's New route */}
      <Route path="/whats-new" element={<ProtectedRoute><WhatsNewPage /></ProtectedRoute>} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AnnouncementProvider>
          <SidebarProvider>
            <AssistancePanelProvider>
              <AppRoutes />
              <Toaster />
            </AssistancePanelProvider>
          </SidebarProvider>
        </AnnouncementProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
